/**
 * Hotkey Settings Schemas
 *
 * Shared Zod schemas for configurable keyboard shortcuts in production stations.
 * Used by both frontend (type inference) and backend (input validation).
 */
import { z } from 'zod';
// ============================================================================
// Binding Types
// ============================================================================
/** Printable ASCII regex — prevents payload bloat and Unicode lookalikes */
const PRINTABLE_ASCII = /^[\x20-\x7E]+$/;
/** Valid modifier keys that can be part of a combo binding */
export const MODIFIER_KEYS = ['ctrl', 'alt', 'meta', 'shift'];
/** Combo binding: modifier(s) + key pressed simultaneously */
export const ComboBindingSchema = z.object({
    type: z.literal('combo'),
    modifiers: z
        .array(z.enum(MODIFIER_KEYS))
        .min(1)
        .refine((mods) => mods.some((m) => m === 'ctrl' || m === 'alt' || m === 'meta'), 'Must include at least one of Ctrl, Alt, or Cmd. Shift alone is not sufficient (scanner data includes shifted characters)'),
    key: z
        .string()
        .min(1)
        .max(30)
        .regex(PRINTABLE_ASCII, 'Must be a printable ASCII character or named key'),
});
/** Hold binding: single key held for a duration */
export const HoldBindingSchema = z.object({
    type: z.literal('hold'),
    key: z
        .string()
        .min(1)
        .max(30)
        .regex(PRINTABLE_ASCII, 'Must be a printable ASCII character or named key'),
    durationMs: z
        .number()
        .int()
        .min(300, 'Minimum hold duration is 300ms (scanners release in <100ms)')
        .max(2000, 'Maximum hold duration is 2000ms'),
});
/** A hotkey binding — either a combo or a hold */
export const HotkeyBindingSchema = z.discriminatedUnion('type', [
    ComboBindingSchema,
    HoldBindingSchema,
]);
// ============================================================================
// Action Registry
// ============================================================================
/**
 * All bindable production station actions.
 * This is a hardcoded registry — not user-extensible.
 */
export const HOTKEY_ACTIONS = [
    // Catcher station
    'catcher_primary', // Accept garment OR undo if already caught (context-dependent)
    'catcher_redo', // Open redo dialog
    'catcher_back', // Clear garment
    // Print station
    'printer_print', // Confirm print (single-print mode only)
    'printer_front', // Print front (dual-print mode only)
    'printer_back', // Print back (dual-print mode only)
    'printer_issue', // Open issue dialog
    'printer_wrong', // Open garment issue dialog (3 reasons: contamination, defect, wrong item)
    'printer_clear', // Clear garment
    // Packing station (main page)
    'packer_ship', // Click primary shipping button
    'packer_manual', // Complete manually
    'packer_copy', // Copy order number
    'packer_clear', // Clear order
    'packer_preview', // Open the big image view for the order
    // Packing station (ShippingPanel success state)
    'packer_confirm', // Confirm shipment / dismiss auto-ship
    'packer_label', // Print/reprint shipping label
];
export const HotkeyActionSchema = z.enum(HOTKEY_ACTIONS);
// ============================================================================
// Station Grouping (single source of truth for conflict detection)
// ============================================================================
/** Maps station names to their action lists. Used by both frontend and backend for conflict detection. */
export const HOTKEY_STATION_ACTIONS = {
    catcher: ['catcher_primary', 'catcher_redo', 'catcher_back'],
    printer: ['printer_print', 'printer_front', 'printer_back', 'printer_issue', 'printer_wrong', 'printer_clear'],
    packer: ['packer_ship', 'packer_manual', 'packer_copy', 'packer_clear', 'packer_preview', 'packer_confirm', 'packer_label'],
};
// ============================================================================
// Config Schema
// ============================================================================
/** Full hotkey configuration: action -> binding (or null for unbound) */
export const HotkeyConfigSchema = z.record(HotkeyActionSchema, HotkeyBindingSchema.nullable());
/**
 * Input schema for updateHotkeySettings tRPC mutation.
 * Wraps the config in an object for forward compatibility.
 */
export const UpdateHotkeySettingsSchema = z.object({
    hotkeys: HotkeyConfigSchema,
});
